import React, {useState, useEffect, useRef} from 'react';
import {RequiredIndicator} from '../ui';
import {FeeType, AdditionalFeesMap} from '../../types/entities';

interface AdditionalFee {
    description: string;
    cost: number;
    type: FeeType;
}

interface PricesSectionProps {
    freight: number;
    additionalFees: AdditionalFeesMap;
    text: string;
    prepaidOrCollect: 'P' | 'C';
    collect: number;
    keepFreight: boolean;
    keepCollect: boolean;
    onFreightChange: (value: number) => void;
    onAdditionalFeesChange: (fees: AdditionalFeesMap) => void;
    onTextChange: (value: string) => void;
    onPrepaidOrCollectChange: (value: 'P' | 'C') => void;
    onCollectChange: (value: number) => void;
    onKeepFreightChange: (value: boolean) => void;
    onKeepCollectChange: (value: boolean) => void;
    disabled?: boolean;
}

export const PricesSection: React.FC<PricesSectionProps> = ({
                                                                freight,
                                                                additionalFees,
                                                                text,
                                                                prepaidOrCollect,
                                                                collect,
                                                                keepFreight,
                                                                keepCollect,
                                                                onFreightChange,
                                                                onAdditionalFeesChange,
                                                                onTextChange,
                                                                onPrepaidOrCollectChange,
                                                                onCollectChange,
                                                                onKeepFreightChange,
                                                                onKeepCollectChange,
                                                                disabled = false
                                                            }) => {
    const [feesList, setFeesList] = useState<AdditionalFee[]>(
        Object.entries(additionalFees || {}).map(([description, value]) => ({
            description,
            cost: typeof value === 'object' ? value.amount : value,
            type: (typeof value === 'object' ? value.type : 'F') as FeeType
        }))
    );

    // Track the last synced additionalFees to detect external changes
    const lastSyncedFeesRef = useRef<string>(JSON.stringify(additionalFees || {}));
    const isInternalUpdateRef = useRef(false);
    const feesContainerRef = useRef<HTMLDivElement>(null);
    const newFeeInputRef = useRef<HTMLInputElement>(null);
    const [pendingScrollToNew, setPendingScrollToNew] = useState(false);

    // Update feesList when additionalFees prop changes from external source (e.g., when loading a car in edit mode)
    useEffect(() => {
        const currentFeesStr = JSON.stringify(additionalFees || {});
        const lastSyncedStr = lastSyncedFeesRef.current;

        // Only update if:
        // 1. The prop actually changed (different from last synced)
        // 2. It's not an internal update (user typing)
        // 3. The new prop represents a complete set (not just a subset from filtering empty descriptions)
        if (currentFeesStr !== lastSyncedStr && !isInternalUpdateRef.current) {
            const newFeesList = Object.entries(additionalFees || {}).map(([description, value]) => ({
                description,
                cost: typeof value === 'object' ? value.amount : value,
                type: (typeof value === 'object' ? value.type : 'F') as FeeType
            }));
            setFeesList(newFeesList);
            lastSyncedFeesRef.current = currentFeesStr;
        }

        // Reset the internal update flag after processing
        isInternalUpdateRef.current = false;
    }, [additionalFees]);

    // Auto-scroll and focus when a new fee is added
    useEffect(() => {
        if (pendingScrollToNew && newFeeInputRef.current) {
            newFeeInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            newFeeInputRef.current.focus();
            setPendingScrollToNew(false);
        }
    }, [pendingScrollToNew, feesList.length]);

    const handleAddFee = () => {
        // Check if there's already an empty entry (no description)
        const hasEmptyEntry = feesList.some(fee => !fee.description.trim());
        if (hasEmptyEntry) {
            // Find and scroll to the empty entry
            const emptyIndex = feesList.findIndex(fee => !fee.description.trim());
            const emptyInput = feesContainerRef.current?.querySelector(`[data-fee-index="${emptyIndex}"]`) as HTMLInputElement;
            if (emptyInput) {
                emptyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                emptyInput.focus();
            }
            return;
        }

        const newFees = [...feesList, {description: '', cost: 0, type: 'F' as FeeType}];
        setFeesList(newFees);
        setPendingScrollToNew(true);
    };

    const handleRemoveFee = (index: number) => {
        const newFees = feesList.filter((_, i) => i !== index);
        setFeesList(newFees);
        updateAdditionalFees(newFees);
    };

    const handleFeeChange = (index: number, field: 'description' | 'cost' | 'type', value: string | number | FeeType) => {
        const newFees = [...feesList];
        newFees[index] = {...newFees[index], [field]: value};
        setFeesList(newFees);
        updateAdditionalFees(newFees);
    };

    const updateAdditionalFees = (fees: AdditionalFee[]) => {
        const feesObj: AdditionalFeesMap = {};
        fees.forEach(fee => {
            if (fee.description.trim()) {
                feesObj[fee.description] = { amount: fee.cost, type: fee.type };
            }
        });
        // Mark that this is an internal update to prevent useEffect from resetting the list
        isInternalUpdateRef.current = true;
        lastSyncedFeesRef.current = JSON.stringify(feesObj);
        onAdditionalFeesChange(feesObj);
    };

    const totalAdditionalFees = feesList.reduce((sum, fee) => sum + (fee.cost || 0), 0);

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1.5 pb-1 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <svg className="w-3.5 h-3.5 mr-1.5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Prices
            </h3>

            <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Freight
                        </label>
                        <div className="flex items-center space-x-2">
                            <input
                                type="number"
                                step="0.01"
                                value={freight || ''}
                                onChange={(e) => onFreightChange(Number(e.target.value))}
                                onWheel={(e) => e.currentTarget.blur()}
                                disabled={disabled}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <label className="flex items-center space-x-1 text-xs text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={keepFreight}
                                    onChange={(e) => onKeepFreightChange(e.target.checked)}
                                    disabled={disabled}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>Keep</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Text
                        </label>
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => onTextChange(e.target.value)}
                            disabled={disabled}
                            placeholder="Optional text..."
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                        />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                            Additional Fees
                        </label>
                        <button
                            type="button"
                            onClick={handleAddFee}
                            disabled={disabled}
                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded-md disabled:bg-gray-400"
                        >
                            + Add Fee
                        </button>
                    </div>
                    <div
                        ref={feesContainerRef}
                        className="space-y-1 max-h-24 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2">
                        {feesList.length === 0 ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                No additional fees added
                            </div>
                        ) : (
                            feesList.map((fee, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                    <input
                                        ref={index === feesList.length - 1 ? newFeeInputRef : undefined}
                                        data-fee-index={index}
                                        type="text"
                                        value={fee.description}
                                        onChange={(e) => handleFeeChange(index, 'description', e.target.value)}
                                        disabled={disabled}
                                        placeholder="Description..."
                                        className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                    />
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={fee.cost || ''}
                                        onChange={(e) => handleFeeChange(index, 'cost', Number(e.target.value))}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        disabled={disabled}
                                        placeholder="Cost..."
                                        className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <div className="flex items-center space-x-2 px-1">
                                        <label className="flex items-center space-x-1 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={fee.type === 'F' || fee.type === 'B'}
                                                onChange={(e) => {
                                                    const isFreightChecked = e.target.checked;
                                                    const isCollectChecked = fee.type === 'C' || fee.type === 'B';
                                                    let newType: FeeType;
                                                    if (isFreightChecked && isCollectChecked) newType = 'B';
                                                    else if (isFreightChecked) newType = 'F';
                                                    else if (isCollectChecked) newType = 'C';
                                                    else newType = 'F'; // Default to F if nothing checked
                                                    handleFeeChange(index, 'type', newType);
                                                }}
                                                disabled={disabled}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span>F</span>
                                        </label>
                                        <label className="flex items-center space-x-1 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={fee.type === 'C' || fee.type === 'B'}
                                                onChange={(e) => {
                                                    const isCollectChecked = e.target.checked;
                                                    const isFreightChecked = fee.type === 'F' || fee.type === 'B';
                                                    let newType: FeeType;
                                                    if (isFreightChecked && isCollectChecked) newType = 'B';
                                                    else if (isFreightChecked) newType = 'F';
                                                    else if (isCollectChecked) newType = 'C';
                                                    else newType = 'F'; // Default to F if nothing checked
                                                    handleFeeChange(index, 'type', newType);
                                                }}
                                                disabled={disabled}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span>C</span>
                                        </label>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFee(index)}
                                        disabled={disabled}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                        </svg>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                    {feesList.length > 0 && (
                        <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 text-right">
                            Total: <span className="font-semibold">{totalAdditionalFees.toFixed(2)}</span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Collect / Prepaid <RequiredIndicator/>
                        </label>
                        <select
                            value={prepaidOrCollect}
                            onChange={(e) => onPrepaidOrCollectChange(e.target.value as 'P' | 'C')}
                            disabled={disabled}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                        >
                            <option value="P">P - Prepaid</option>
                            <option value="C">C - Collect</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Collect
                        </label>
                        <div className="flex items-center space-x-2">
                            <input
                                type="number"
                                step="0.01"
                                value={collect || ''}
                                onChange={(e) => onCollectChange(Number(e.target.value))}
                                onWheel={(e) => e.currentTarget.blur()}
                                disabled={disabled || prepaidOrCollect === 'P'}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                            <label className="flex items-center space-x-1 text-xs text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={keepCollect}
                                    onChange={(e) => onKeepCollectChange(e.target.checked)}
                                    disabled={disabled || prepaidOrCollect === 'P'}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>Keep</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
